import { CustomButton } from '@ui-kit'
import {
  useAppSelector,
  useAppDispatch,
  RootState,
} from '../../utils/agent.store'
import {
  SendConsentWorkFlowPropsType,
  TriggerConsentRequestPayload,
} from '../../types/workflow.types'
import {
  addToaster,
  getGenericEntityListingAPI,
  RelationshipFormattedTreeTypes,
  triggerConsentRequestApi,
  WorkflowInstanceTaskType,
  WorkflowProcessType,
} from '@app-store'
import { NAMESPACES } from '../../utils/constants'

import { v4 as uuid } from 'uuid'
import { useState } from 'react'
import { isReadOnlyUser } from '../../utils/permission'
import { permissionObject } from '../../utils/helperFunctions'

const SendConsentTask = ({
  dataQaId,
  recipientType,
  task,
  tabIndex,
}: SendConsentWorkFlowPropsType) => {
  const dispatch = useAppDispatch()
  const [isSent, setIsSent] = useState(false)
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions?.programPermissions
  )
  const caseData = useAppSelector(
    (state: RootState) => state.workflowData?.caseData
  )
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)
  const relationshipTree = useAppSelector(
    (state) => state?.workflowData?.relationshipTree
  )
  const getEntityFromTree = (
    entityKey: string,
    relationshipTree: RelationshipFormattedTreeTypes[],
    workflowDefinition: WorkflowProcessType
  ) => {
    const targetId =
      workflowDefinition?.global_variables?.entities?.[entityKey]?.entity_id
    return relationshipTree?.find(
      (item) => item?.entity_id?.toString() === targetId
    )
  }

  const buildConsentPayload = (
    task: WorkflowInstanceTaskType,
    entityData: { email: string | undefined },
    programData: { id: number },
    recipientType: string
  ): TriggerConsentRequestPayload => ({
    entity_type: 'CASE',
    entity_record_id: Number(task?.case_id),
    program_id: programData?.id,
    recipient_type: recipientType.toUpperCase(),
    email_template_id: Number(task?.identifier?.template_id),
    recipient_email: entityData?.email ?? '',
    service_id: Number(
      workflowDefinition?.global_variables?.inherited_variables?.service_id ?? 1
    ),
  })

  const handleTriggerConsentRequest = async (recipientType: string) => {
    try {
      if (!workflowDefinition?.global_variables?.entities || !task?.case_id)
        return

      const isPatient = recipientType === NAMESPACES?.PATIENT
      const programEntity = getEntityFromTree(
        'program',
        relationshipTree,
        workflowDefinition
      )
      const targetEntityKey = isPatient ? 'patients' : 'providers'
      const targetEntity = getEntityFromTree(
        targetEntityKey,
        relationshipTree,
        workflowDefinition
      )

      if (!programEntity?.field_name || !targetEntity?.field_name) return

      const filtredResponse = await getGenericEntityListingAPI({
        entity_name: 'case',
        select: [programEntity.field_name, targetEntity.field_name, 'id'],
        filters: [
          {
            logical_operator: 'AND',
            criteria: [
              {
                field_name: 'id',
                operation: 'EQUALS',
                values: [task.case_id],
              },
            ],
          },
        ],
        related_entities: {
          [targetEntity.field_name]: {
            select: ['field_name', 'email'],
            criteria: [],
          },
        },
      })

      const caseData = filtredResponse?.[0]
      if (!caseData) return

      const entityData = caseData[targetEntity.field_name]
      const programData = caseData[programEntity.field_name]
      if (!entityData?.email || !entityData?.id) return

      const payload = buildConsentPayload(
        task,
        entityData,
        programData,
        recipientType
      )
      try {
        await triggerConsentRequestApi(payload)
        setIsSent(true)
        dispatch(
          addToaster({
            id: uuid(),
            type: 'success',
            message: NAMESPACES.MAIL_SUCCESS,
          })
        )
      } catch (error) {
        dispatch(
          addToaster({
            id: uuid(),
            type: 'error',
            message: NAMESPACES.MAIL_ERROR,
          })
        )
      }
    } catch (error) {
      console.error(error)
    }
  }

  const isConsentAllowed = () => {
    return permissionObject.Manage_Workflow(
      programPermissions,
      caseData,
      task,
      userId ?? ''
    )
  }

  return (
    <div className="flex justify-end p-2">
      <CustomButton
        type="button"
        isFilled={true}
        horizontalPaddingClasses="px-4.5"
        verticalPaddingClasses="py-2"
        dataQaId={dataQaId}
        widthClass="w-[7.5rem]"
        onClick={() => handleTriggerConsentRequest(recipientType)}
        tabIndex={tabIndex}
        disabled={isReadOnlyUser() || !isConsentAllowed()}
      >
        {isSent ? NAMESPACES.RESEND_CONSENT_BUTTON : NAMESPACES?.CONSENT_BUTTON}
      </CustomButton>
    </div>
  )
}

export default SendConsentTask
